import { Component, Inject, ChangeDetectorRef, OnInit } from '@angular/core';
import {
	MAT_DIALOG_DATA,
	MatDialogRef,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { ApiLoadingKeys } from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDivider } from '@angular/material/list';
import { CdkScrollable } from '@angular/cdk/scrolling';

export interface LoadingConfirmationDialogComponentData {
	// api.services.ts related information about async action
	apiFunctionToExecute: Function;
	loadingKeyOfApiFunction: ApiLoadingKeys;

	// Text content of the dialog popup
	headerIcon?: string;
	dialogDivider?: boolean;
	disableContinueUponOpen?: boolean;
	headerTextBeforeLoading: string;
	bodyTextBeforeLoading: string;
	negativeButtonDialogText: string;
	positiveButtonDialogText: string;

	// Button classes that can be used for the popup
	negativeButtonClass:
		'primary-small-button' | 'secondary-small-button' | 'warning-small-button';
	positiveButtonClass:
		| 'primary-small-button'
		| 'secondary-small-button'
		| 'warning-small-button'
		| 'warning-small-button-wider';
}

/**
 * Generic dialog component used for:
 * 1. Yes/no user confirmation prompt of an async action they take
 *    (i.e. "Please confirm you want to delete (1) Control Assignment.")
 * 2. Showing the async action being completed using a loading spinner
 * 3. Executing the async action
 * 4. As of right now, does NOT show a confirmation that the action was completed successfully.
 *    Only shows if this action errored out.
 */
@Component({
	selector: 'app-loading-confirmation-dialog',
	templateUrl: './loading-confirmation-dialog.component.html',
	styleUrls: [
		'./loading-confirmation-dialog.component.scss',
		'../../ui/common/main.scss',
	],
	imports: [
		MatIcon,
		MatProgressSpinner,
		MatDivider,
		CdkScrollable,
		MatDialogContent,
		MatDialogActions,
	],
})
export class LoadingConfirmationDialogComponent implements OnInit {
	errorSavingChanges?: boolean;
	dialogDivider = false;
	disableContinueUponOpen = false;
	isContinueDisabled = false;

	constructor(
		private dialogRef: MatDialogRef<LoadingConfirmationDialogComponent>,
		public readonly loadingService: LoadingIndicatorService,
		@Inject(MAT_DIALOG_DATA)
		public data: LoadingConfirmationDialogComponentData,
		private changeDetectorRef: ChangeDetectorRef
	) {
		if (data.dialogDivider) {
			this.dialogDivider = true;
			this.disableContinueUponOpen = data.disableContinueUponOpen ?? false;
		}
	}

	ngOnInit(): void {
		if (this.disableContinueUponOpen) {
			this.isContinueDisabled = true;
			setTimeout(() => {
				this.isContinueDisabled = false;
				this.changeDetectorRef.detectChanges();
			}, 3000); // Added the 3 Seconds disable in the button for continue, and before it's a 1.5 Seconds (1500).
		}
	}

	async onConfirmClicked() {
		if (this.loadingService.isLoading(this.data.loadingKeyOfApiFunction)) {
			return;
		}
		await this.data
			.apiFunctionToExecute()
			.then((res: any) => {
				this.dialogRef.close(true);
			})
			.catch((err: any) => {
				console.error(err);
				this.errorSavingChanges = true;
			});
	}

	onCloseClicked(): void {
		this.dialogRef.close(false);
	}
}
